import SearchableSelect from './search/SearchableSelect';
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Box,
  Typography,
  Alert,
  Divider,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { createTransfer } from '../services/api';

function TransferModal({ open, onClose, accounts, categories, onTransferComplete }) {
  const [fromAccountId, setFromAccountId] = useState('');
  const [fromCategoryId, setFromCategoryId] = useState('');
  const [toAccountId, setToAccountId] = useState('');
  const [toCategoryId, setToCategoryId] = useState('');
  const [amount, setAmount] = useState('');
  const [transactionDate, setTransactionDate] = useState(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFromAccountId('');
      setFromCategoryId('');
      setToAccountId('');
      setToCategoryId('');
      setAmount('');
      setTransactionDate(new Date().toISOString().split('T')[0]);
      setDescription('');
      setError('');
    }
  }, [open]);

  // Filter to only depository accounts (no credit cards)
  const depositoryAccounts = (accounts || []).filter(
    (a) => a.account_type !== 'credit' && a.account_status !== 'archived'
  );

  // Get categories for selected account
  const fromCategories = (categories || []).filter(
    (c) => c.account_id === fromAccountId && !c.is_deleted
  );
  const toCategories = (categories || []).filter(
    (c) => c.account_id === toAccountId && !c.is_deleted
  );

  const handleSubmit = async () => {
    if (!fromAccountId || !toAccountId || !amount || !transactionDate) {
      setError('Please fill in all required fields');
      return;
    }
    if (fromAccountId === toAccountId && fromCategoryId === toCategoryId) {
      setError('Source and destination must be different');
      return;
    }
    if (parseFloat(amount) <= 0) {
      setError('Amount must be positive');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await createTransfer({
        from_account_id: fromAccountId,
        from_category_id: fromCategoryId || null,
        to_account_id: toAccountId,
        to_category_id: toCategoryId || null,
        amount: parseFloat(amount),
        transaction_date: transactionDate,
        description: description || null,
      });
      if (onTransferComplete) onTransferComplete();
      onClose();
    } catch (err) {
      setError(err.response?.data?.msg || 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  const getAccountName = (id) => {
    const acct = depositoryAccounts.find((a) => a.id === id);
    return acct ? acct.name : '';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SwapHorizIcon color="primary" />
        Transfer Between Accounts
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>

          {error && <Alert severity="error">{error}</Alert>}

          {/* FROM */}
          <Typography variant="subtitle2" color="text.secondary">From</Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Source Account</InputLabel>
            <SearchableSelect
              value={fromAccountId}
              onChange={(e) => { setFromAccountId(e.target.value); setFromCategoryId(''); }}
              label="Source Account"
            >
              {depositoryAccounts.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
              ))}
            </SearchableSelect>
          </FormControl>

          {fromAccountId && fromCategories.length > 0 && (
            <FormControl fullWidth size="small">
              <InputLabel>Source Category (optional)</InputLabel>
              <SearchableSelect
                value={fromCategoryId}
                onChange={(e) => setFromCategoryId(e.target.value)}
                label="Source Category (optional)"
              >
                <MenuItem value=""><em>No specific category</em></MenuItem>
                {fromCategories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
          )}

          <Divider>
            <SwapHorizIcon color="action" />
          </Divider>

          {/* TO */}
          <Typography variant="subtitle2" color="text.secondary">To</Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Destination Account</InputLabel>
            <SearchableSelect
              value={toAccountId}
              onChange={(e) => { setToAccountId(e.target.value); setToCategoryId(''); }}
              label="Destination Account"
            >
              {depositoryAccounts.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
              ))}
            </SearchableSelect>
          </FormControl>

          {toAccountId && toCategories.length > 0 && (
            <FormControl fullWidth size="small">
              <InputLabel>Destination Category (optional)</InputLabel>
              <SearchableSelect
                value={toCategoryId}
                onChange={(e) => setToCategoryId(e.target.value)}
                label="Destination Category (optional)"
              >
                <MenuItem value=""><em>No specific category</em></MenuItem>
                {toCategories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
          )}

          <Divider />

          {/* Amount and Date */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              size="small"
              fullWidth
              inputProps={{ min: 0, step: '0.01' }}
            />
            <TextField
              label="Date"
              type="date"
              value={transactionDate}
              onChange={(e) => setTransactionDate(e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="small"
            fullWidth
            placeholder="e.g., Rent payment to Chase"
          />

          {/* Preview */}
          {fromAccountId && toAccountId && amount && (
            <Alert severity="info" icon={<SwapHorizIcon />}>
              ${parseFloat(amount || 0).toFixed(2)} from <strong>{getAccountName(fromAccountId)}</strong> to <strong>{getAccountName(toAccountId)}</strong>
              <br />
              <Typography variant="caption" color="text.secondary">
                Both sides tagged as internal transfer — excluded from income/expense analytics
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !fromAccountId || !toAccountId || !amount}
          startIcon={<SwapHorizIcon />}
        >
          {loading ? 'Transferring...' : 'Transfer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TransferModal;
